/**
 * Unified Authentication Utility
 * Uses httpOnly cookies for secure session management
 */
import {
  PAYMENTS_DISABLED,
  isPaymentInitiationRequest,
  paymentsUnavailableResponse,
} from './paymentAvailability';

// Use relative URL if REACT_APP_BACKEND_URL is not set (for same-origin requests)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Unified fetch wrapper that automatically includes cookies
 * All requests use credentials: 'include' to send httpOnly cookies
 *
 * Uses the pristine window.fetch saved in index.html, so response bodies stay
 * readable even if a browser extension wraps fetch.
 */
// eslint-disable-next-line no-undef
const rawFetch = (typeof window !== 'undefined' && window.__origFetch) || fetch;

export const authFetch = async (url, options = {}) => {
  // This is the application request boundary for every current real-money
  // entry point. In a guarded staging build, reject before rawFetch can make
  // a network request. Refunds, cancellations and callback reconciliation are
  // intentionally not initiation and therefore remain available.
  if (PAYMENTS_DISABLED && isPaymentInitiationRequest(url, options)) {
    return paymentsUnavailableResponse();
  }

  // Check if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;

  // 🌍 Forward the user's timezone offset on every request so server-side
  // streak/heartbeat/midnight calculations match the user's wall clock.
  // `Date.getTimezoneOffset()` returns minutes UTC is AHEAD of local — i.e.
  // PST = +480, BST = -60, UTC = 0. Backend reads it from `X-TZ-Offset-Min`
  // and uses it to anchor "today" properly. Feb 22 2026.
  let tzOffsetMin = 0;
  try {
    tzOffsetMin = new Date().getTimezoneOffset();
  } catch {
    /* noop */
  }

  const defaultOptions = {
    credentials: 'include', // Always include cookies
    headers: {
      // Don't set Content-Type for FormData - browser will set it with boundary
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      'X-TZ-Offset-Min': String(tzOffsetMin),
      ...(options.headers || {})
    }
  };

  // Merge user options
  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {})
    }
  };

  const response = await rawFetch(url, finalOptions);
  return response;
};

/**
 * Check if user is authenticated
 * Returns user object if authenticated, null otherwise
 */
export const checkAuth = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
      credentials: 'include' // Send httpOnly cookie
    });

    if (!response.ok) {
      // Auth failed - clear any stale data
      clearAuthData();
      return null;
    }

    const userData = await response.json();
    return userData;
  } catch (error) {
    console.error('Auth check failed:', error);
    clearAuthData();
    return null;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  localStorage.removeItem('userRole');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('username');
  localStorage.removeItem('admin');
  localStorage.removeItem('writer');
  localStorage.removeItem('assessment_completed');
};

/**
 * Logout user - clear local data and call backend logout
 */
export const logout = async () => {
  try {
    // Call backend logout endpoint
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Always clear local auth data
    clearAuthData();
  }
};

/**
 * Get auth headers for fetch requests
 * Note: With httpOnly cookies, we only need credentials: 'include'
 */
export const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Format API error detail for display
 * Handles FastAPI validation errors that return arrays
 */
export const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};
